import React from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { getBillingCommitmentPath } from '@sola/my-sdk/constants.ts';
import type { GraphQLPledge } from '@sola/schema/dotcom/supporters/schema.generated.ts';

import { IconTile } from '../../atoms/icon-tile/icon-tile.tsx';
import beyondMeasureLogo from '../../images/pledge-beyond-measure.png';
import { formatPledgeName } from '../../utils/pledges.ts';
import {
  AffinityCardGraphic,
  AffinityCardInteractive,
  AffinityCardStat,
  AffinityCardStats,
  AffinityCardSummary,
} from '../affinity-card/affinity-card.tsx';

export type AffinityCardPledgeProps = {
  pledge: GraphQLPledge;
};

export const AffinityCardPledge: React.FC<AffinityCardPledgeProps> = ({
  pledge,
}) => {
  const { t } = useTranslation(['my', 'core']);

  const title = formatPledgeName(pledge.pledgeName, t);
  const percentComplete =
    pledge.totalPledgeAmount > 0
      ? Math.round((pledge.totalGivenToDate / pledge.totalPledgeAmount) * 100)
      : 0;

  return (
    <AffinityCardInteractive>
      <Link className="w-full" href={getBillingCommitmentPath(pledge.pledgeId)}>
        <AffinityCardGraphic>
          <IconTile imgSrc={beyondMeasureLogo} alt={title} size="lg" />
        </AffinityCardGraphic>
        <AffinityCardSummary
          title={title}
          summary={t('my:homepage.affinityCard.pledge.subtitle')}
        />
        <AffinityCardStats>
          <AffinityCardStat
            title={t('my:homepage.affinityCard.pledge.complete')}
            stat={t('core:numbers.percent', { value: percentComplete })}
          />
          <AffinityCardStat
            title={t('my:homepage.affinityCard.pledge.given')}
            stat={t('core:numbers.currencyCustom', {
              amount: pledge.totalGivenToDate,
            })}
          />
          <AffinityCardStat
            title={t('my:homepage.affinityCard.pledge.goal')}
            stat={t('core:numbers.currencyCustom', {
              amount: pledge.totalPledgeAmount,
            })}
          />
        </AffinityCardStats>
      </Link>
    </AffinityCardInteractive>
  );
};
